import { useEffect, useState } from "react";
import { useReportConfig, useReportData, useReportMeta, useReportSnapshot } from "../ReportLandingContext";
import { useShareReportMutation } from "../queries";
import { SNAPSHOT_LIST_WIDGET_CAP } from "../constants";

// Widgets that render purely from their config (no data fetch) — they need no snapshot
// entry and must render normally on the public page. See SHARED_REPORTS_DESIGN.md.
export const WIDGETS_WITHOUT_DATA = ["WidgetRichText", "WidgetVisualize"];

// Marker stored in the snapshot for a widget that failed/timed out during capture.
// WidgetRenderer renders an "unavailable" placeholder for these instead of the widget.
export const SNAPSHOT_UNAVAILABLE_MARKER = "__snapshotUnavailable";

// List widgets push { pages: [{ data: [...rows] }] } to the store. Public snapshots cap
// these rows to keep the doc small. This cap is a PUBLIC-REPORTS concern, so it lives here
// (the capture logic) — the widgets themselves stay agnostic and push their full data.
const LIST_WIDGETS = ["WidgetResponsesList", "WidgetAnnotationExplorer"];

const capListWidgetData = (widgetData) => {
  const page = widgetData?.pages?.[0];
  if (!page?.data) return widgetData;
  return {
    ...widgetData,
    pages: [{ ...page, data: page.data.slice(0, SNAPSHOT_LIST_WIDGET_CAP) }],
  };
};

// Build the { [widgetId]: data } snapshot from what each widget pushed to the central
// store (widgetDataStore). Every DATA widget gets an entry — its captured data, or an
// "unavailable" marker if it never produced data (errored/timed out). Static widgets are
// skipped (they render from config on the public page, and would otherwise try to fetch).
export const buildReportSnapshot = ({ config, widgetDataStore }) => {
  const snapshot = {};
  (config || []).forEach((widget) => {
    if (WIDGETS_WITHOUT_DATA.includes(widget.comp)) return;
    const wid = widget.id;
    if (!wid) return;
    if (widgetDataStore[wid] === undefined) {
      snapshot[wid] = { [SNAPSHOT_UNAVAILABLE_MARKER]: true };
      return;
    }
    snapshot[wid] = LIST_WIDGETS.includes(widget.comp)
      ? capListWidgetData(widgetDataStore[wid])
      : widgetDataStore[wid];
  });
  return snapshot;
};

// Are all widgets in `config` done loading (success or error)? Uses the existing
// loadedWidgetsTracker, which every widget reports into.
const allWidgetsLoaded = ({ config, loadedWidgetsTracker }) => {
  if (!config) return false;
  // An empty report has nothing to wait for — returning false here would leave
  // the Share button on "Preparing link…" forever. It shares as an empty page.
  if (config.length === 0) return true;
  return config.every((_, idx) =>
    loadedWidgetsTracker.some(
      (t) => t.index === idx && (t.status === "success" || t.status === "error")
    )
  );
};

/**
 * Orchestrates "share for all to view": wait until every widget has finished loading
 * (the sequential waterfall force-loads them top-to-bottom), read the captured data from
 * the central store, freeze it into a snapshot, and POST it to create the public link.
 *
 * Returns { initiateShare, isPreparing, isSharing, shareUrl }.
 */
export const useShareReport = () => {
  const { config } = useReportConfig();
  const { reportData } = useReportData();
  const { localReportMeta } = useReportMeta();
  const { widgetDataStore, loadedWidgetsTracker } = useReportSnapshot();
  const shareMutation = useShareReportMutation();

  // "preparing" = user clicked share, we're waiting for all widgets to finish loading
  // before we can capture a complete snapshot.
  const [isPreparing, setIsPreparing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  const ready = allWidgetsLoaded({ config, loadedWidgetsTracker });

  useEffect(() => {
    if (!isPreparing || !ready) return;
    setIsPreparing(false);

    const snapshot = buildReportSnapshot({ config, widgetDataStore });
    shareMutation.mutate(
      {
        reportId: reportData?._id,
        title: localReportMeta.title,
        subtitle: localReportMeta.subtitle,
        config,
        snapshot,
      },
      {
        onSuccess: ({ token }) => {
          // Setting shareUrl opens the share modal; the user copies from there.
          setShareUrl(`${window.location.origin}/public/report/${token}`);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreparing, ready]);

  return {
    initiateShare: () => setIsPreparing(true),
    isPreparing,
    isSharing: shareMutation.isLoading,
    shareUrl,
    closeShareModal: () => setShareUrl(null),
  };
};
